import { createSelector } from '@reduxjs/toolkit'

import type { QueryResultLike } from '@krp/client-runtime'

import type { NativeRead } from '../api/envelope'
import { formatMoney, formatQuantity } from './format'
import type { PriceList } from './priceList'
import type { PriceByType, SectionVm, WarehouseStock } from './types'
import { sectionOf } from './viewKit'

/**
 * Цены и остатки в карточке товара — ТОЛЬКО ЧТЕНИЕ: розничная цена, остаток всего и по складам. Закупки здесь не
 * записываются и не показываются: они живут в поставках (решение владельца 22.10.2026), отдельного окна цен товара нет.
 */
export interface ProductFactsInput {
  stock: QueryResultLike<NativeRead<readonly WarehouseStock[]>>
  prices: QueryResultLike<NativeRead<readonly PriceByType[]>>
  /** Прайс базы ради одного: имён видов цен (у цены товара в ответе только ключ вида). */
  priceTypes: QueryResultLike<NativeRead<PriceList>>
}

export interface ProductFactsVm {
  prices: SectionVm<{ key: string; label: string; priceText: string }>
  stock: SectionVm<{ warehouse: string; quantityText: string }> & { totalText: string | null }
}

/** Прайс для имён видов цен: строки не нужны, нужен список видов. */
export const PRICE_TYPES_PAGE_SIZE = 1

const NO_ROWS: readonly never[] = []

/** Ключ вида цен без имени — коротко, чтобы строка не была GUID-ом во всю ширину телефона. */
const unnamed = (key: string): string => `Вид цен ${key.slice(0, 8)}`

export const makeSelectProductFactsVm = () => {
  const selectNames = createSelector([(input: ProductFactsInput) => input.priceTypes.data], (read) => {
    const names = new Map<string, string>()
    for (const type of read?.data.priceTypes ?? []) if (type.name !== null && type.name.trim() !== '') names.set(type.key, type.name)
    return names
  })
  const selectPrices = createSelector([(input: ProductFactsInput) => input.prices, selectNames], (query, names) =>
    sectionOf<readonly PriceByType[], { key: string; label: string; priceText: string }>(
      query,
      (rows) =>
        rows.map((row) => ({
          key: row.priceTypeRefKey,
          label: names.get(row.priceTypeRefKey) ?? unnamed(row.priceTypeRefKey),
          priceText: formatMoney(row.price),
        })),
      NO_ROWS,
    ),
  )
  const selectStock = createSelector([(input: ProductFactsInput) => input.stock], (query) => {
    const section = sectionOf<readonly WarehouseStock[], { warehouse: string; quantityText: string }>(
      query,
      (rows) => rows.map((row) => ({ warehouse: row.warehouseName, quantityText: formatQuantity(row.quantity) })),
      NO_ROWS,
    )
    const totalText =
      section.query.status === 'ready' && query.data !== undefined
        ? formatQuantity(query.data.data.reduce((sum, row) => sum + row.quantity, 0))
        : null
    return { ...section, totalText }
  })
  return createSelector([selectPrices, selectStock], (prices, stock): ProductFactsVm => ({ prices, stock }))
}
